import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { MessagesProvider } from 'pages/TicketsPage/components/TicketDetailsPopup/components/Messages/contexts/MessagesContext';
import { TicketDetailsPopup } from 'pages/TicketsPage/components/TicketDetailsPopup/TicketDetailsPopup';

const TicketDetailViewPage: FC = (): JSX.Element => {
  const { ticketId: urlTicketId } = useParams<{ ticketId: string }>();
  const location = useLocation();
  const {
    setTicketId,
    selectedTicketId,
    openedTicketTabs,
    openedScheduleTicketTabs,
    closeTicketTab,
    closeScheduleTicketTab,
    activeTab,
  } = useTickets();
  const navigate = useNavigate();

  useEffect(() => {
    if (urlTicketId) {
      setTicketId(urlTicketId);
    }
    return (): void => {
      setTicketId(null);
    };
  }, [urlTicketId, setTicketId]);

  const handleClose = useCallback((): void => {
    if (!urlTicketId) return;

    const isScheduleSection =
      activeTab === ActiveTabTypeE.Schedule ||
      new URLSearchParams(location.search).get('section') === 'schedule';
    const currentTabs = isScheduleSection ? openedScheduleTicketTabs : openedTicketTabs;
    const tabLabel = Object.entries(currentTabs).find(([, id]) => id === urlTicketId)?.[0];

    if (tabLabel) {
      if (isScheduleSection) {
        closeScheduleTicketTab(tabLabel);
      } else {
        closeTicketTab(tabLabel);
      }
    }

    const cleanSearch = new URLSearchParams(location.search);
    cleanSearch.delete('section');
    cleanSearch.delete('tabLabel');
    const searchStr = cleanSearch.toString();
    navigate({ pathname: RoutePathsE.TICKETS_PAGE, search: searchStr ? `?${searchStr}` : '' });
  }, [
    urlTicketId,
    activeTab,
    location.search,
    openedTicketTabs,
    openedScheduleTicketTabs,
    closeTicketTab,
    closeScheduleTicketTab,
    navigate,
  ]);

  const activeTicketId = selectedTicketId || urlTicketId || '';

  if (!urlTicketId) {
    return <></>;
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <MessagesProvider ticketId={activeTicketId}>
        <TicketDetailsPopup usePageLayout onClose={handleClose} />
      </MessagesProvider>
    </div>
  );
};

export default TicketDetailViewPage;
